import { ReactNode, useEffect, useState } from "react";
import { useBreakpointValue } from "../../hooks/use-breakpoint-value";
import { useDebouncedValue } from "rooks";
import { CloseIcon, SearchIcon } from "../../icons";
import { cn } from "../../utils/class-names";

interface ToolbarProps {
  className?: string;
  onPatternChange: (pattern: string | null) => void;
  sortMenu: ReactNode;
}

export function Toolbar({
  className,
  onPatternChange,
  sortMenu,
}: ToolbarProps): JSX.Element {
  const [pattern, setPattern] = useState("");
  const reset = () => setPattern("");

  const [isFocused, setIsFocused] = useState(false);
  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  const hideSortOnActiveSearch = useBreakpointValue(true, { sm: false });
  const searchInputSize = useBreakpointValue(1, { sm: 20 });

  const isSearchActive = !!pattern || isFocused;
  // TODO: calculate these values dynamically using theme values
  const outlinedChildClasses = isSearchActive
    ? "px-4 py-2.5 focus-within:border-blue-400"
    : "px-4 py-2.5";

  const hideSortButton = hideSortOnActiveSearch && isSearchActive;

  const showCards = useBreakpointValue(true, { md: false });

  const showSortButton = !hideSortButton && showCards;

  const [debouncedPattern] = useDebouncedValue(pattern, 250);

  useEffect(() => {
    onPatternChange(debouncedPattern || null);
  }, [onPatternChange, debouncedPattern]);

  /**
   * TODO: on small mobile devices, animate the width transition of the search bar once it is active
   */
  return (
    <div className={cn("flex gap-3 md:gap-4", className)}>
      <div className="flex-1">
        <div
          className={cn(
            "flex gap-3 items-center text-shale-200 rounded-lg bg-shale-700 hover:bg-shale-500 hover:ease-in duration-100 border-2 border-transparent",
            outlinedChildClasses
          )}
        >
          <SearchIcon className="w-5 h-5" />
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            className="flex-1 text-white bg-transparent placeholder:text-shale-200"
            placeholder="Search for a market"
            size={searchInputSize}
          />
          <div className="flex items-center w-5 h-5">
            {!!pattern && (
              <button type="button" onClick={reset} aria-label="Clear search">
                <CloseIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {showSortButton && sortMenu}
    </div>
  );
}
